/* global gdAlertify */
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../config/axiosConfig";
import { MOSTRAR_RECORDATORIOS_VALORES } from "../../config/featureFlags";

const emptyForm = {
    id: null,
    fecha_desde: "",
    fecha_hasta: "",
    observacion: "",
    recordatorio_valor_turno: "",
    recordatorio_movilizacion: "",
    recordatorio_colacion: "",
};

const formatDate = (value) => {
    if (!value) return "Sin fecha";
    const [year, month, day] = value.split("-");
    return `${day}-${month}-${year}`;
};

const isActiveNow = (item) => {
    const today = new Date().toISOString().split("T")[0];
    return (!item.fecha_desde || item.fecha_desde <= today) && (!item.fecha_hasta || item.fecha_hasta >= today);
};

const getStatus = (item) => {
    const today = new Date().toISOString().split("T")[0];
    if (isActiveNow(item)) return { className: "bg-success", label: "Activo ahora" };
    if (item.fecha_desde && item.fecha_desde > today) return { className: "bg-primary", label: "Futuro" };
    return { className: "bg-secondary", label: "Vencido" };
};

const rangesOverlap = (a, b) => {
    const aStart = a.fecha_desde || "1000-01-01";
    const aEnd = a.fecha_hasta || "9999-12-31";
    const bStart = b.fecha_desde || "1000-01-01";
    const bEnd = b.fecha_hasta || "9999-12-31";
    return aStart <= bEnd && aEnd >= bStart;
};

const hasContent = (item) => [
    item.observacion,
    item.recordatorio_valor_turno,
    item.recordatorio_movilizacion,
    item.recordatorio_colacion,
].some(value => String(value || "").trim() !== "");

const ModalRecordatoriosPaciente = ({ isOpen, onClose, pacienteId, pacienteNombre, onSaved }) => {
    const [recordatorios, setRecordatorios] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetForm = () => setForm(emptyForm);

    const normalizeList = (data) => {
        if (Array.isArray(data?.recordatorios)) return data.recordatorios;
        if (data && hasContent(data)) return [data];
        return [];
    };

    const fetchRecordatorios = useCallback(async () => {
        if (!pacienteId) return;

        setLoading(true);
        try {
            const response = await axiosInstance.get(`/paciente/${pacienteId}/recordatorio`);
            setRecordatorios(normalizeList(response.data?.data));
        } catch (error) {
            console.error("Error al cargar recordatorios:", error);
            setRecordatorios([]);
        } finally {
            setLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        if (isOpen && pacienteId) {
            resetForm();
            fetchRecordatorios();
        }
    }, [fetchRecordatorios, isOpen, pacienteId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEdit = (item) => {
        setForm({
            id: item.id || null,
            fecha_desde: item.fecha_desde || "",
            fecha_hasta: item.fecha_hasta || "",
            observacion: item.observacion || "",
            recordatorio_valor_turno: item.recordatorio_valor_turno || "",
            recordatorio_movilizacion: item.recordatorio_movilizacion || "",
            recordatorio_colacion: item.recordatorio_colacion || "",
        });
    };

    const validateForm = () => {
        if (!form.fecha_desde || !form.fecha_hasta) {
            gdAlertify.notify("Debe indicar fecha desde y fecha hasta.", "error");
            return false;
        }

        if (form.fecha_desde > form.fecha_hasta) {
            gdAlertify.notify("La fecha desde no puede ser mayor que la fecha hasta.", "error");
            return false;
        }

        if (!hasContent(form)) {
            gdAlertify.notify("Debe ingresar al menos un texto de recordatorio.", "error");
            return false;
        }

        const overlap = recordatorios.some(item => {
            if (String(item.id || "") === String(form.id || "")) return false;
            return rangesOverlap(item, form);
        });

        if (overlap) {
            gdAlertify.notify("Las fechas se cruzan con otro recordatorio del paciente.", "error");
            return false;
        }

        return true;
    };

    const handleGuardar = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            gdAlertify.showLoader();
            const response = await axiosInstance.post("/paciente/recordatorio/guardar", {
                ...form,
                pacienteId,
            });

            if (response.data?.error) {
                gdAlertify.notify(response.data.message || "Error al guardar el recordatorio.", "error");
                return;
            }

            gdAlertify.notify("Recordatorio actualizado.", "success");
            resetForm();
            await fetchRecordatorios();
            if (onSaved) onSaved();
        } catch (error) {
            console.error("Error al guardar recordatorio:", error);
            gdAlertify.notify("Error al guardar el recordatorio.", "error");
        } finally {
            gdAlertify.hideLoader();
            setIsSubmitting(false);
        }
    };

    const handleEliminar = (item) => {
        gdAlertify.confirm({
            title: "Eliminar recordatorio",
            message: "¿Está seguro de eliminar este recordatorio?",
            onConfirm: async () => {
                setIsSubmitting(true);
                try {
                    gdAlertify.showLoader();
                    const response = await axiosInstance.post("/paciente/recordatorio/eliminar", {
                        id: item.id,
                        pacienteId,
                    });

                    if (response.data?.error) {
                        gdAlertify.notify(response.data.message || "Error al eliminar el recordatorio.", "error");
                        return;
                    }

                    gdAlertify.notify("Recordatorio eliminado.", "success");
                    if (String(form.id || "") === String(item.id || "")) resetForm();
                    await fetchRecordatorios();
                    if (onSaved) onSaved();
                } catch (error) {
                    console.error("Error al eliminar recordatorio:", error);
                    gdAlertify.notify("Error al eliminar el recordatorio.", "error");
                } finally {
                    gdAlertify.hideLoader();
                    setIsSubmitting(false);
                }
            },
        });
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
            <div className="modal-dialog modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Recordatorios - {pacienteNombre}</h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
                    </div>
                    <div className="modal-body">
                        <div className="border rounded p-3 mb-4 bg-light">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0 fw-bold">{form.id ? "Editar recordatorio" : "Nuevo recordatorio"}</h6>
                                {form.id && (
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetForm} disabled={isSubmitting}>
                                        Nuevo
                                    </button>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-3 mb-3">
                                    <label className="form-label fw-bold">Fecha desde</label>
                                    <input type="date" className="form-control" name="fecha_desde" value={form.fecha_desde} onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <label className="form-label fw-bold">Fecha hasta</label>
                                    <input type="date" className="form-control" name="fecha_hasta" value={form.fecha_hasta} onChange={handleChange} disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Recordatorio general</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    name="observacion"
                                    placeholder="Notas o recordatorios para este paciente..."
                                    value={form.observacion}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                ></textarea>
                            </div>

                            {MOSTRAR_RECORDATORIOS_VALORES && <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Valor turno</label>
                                    <textarea className="form-control" rows="3" name="recordatorio_valor_turno" value={form.recordatorio_valor_turno} onChange={handleChange} disabled={isSubmitting}></textarea>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Movilización</label>
                                    <textarea className="form-control" rows="3" name="recordatorio_movilizacion" value={form.recordatorio_movilizacion} onChange={handleChange} disabled={isSubmitting}></textarea>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label fw-bold">Colación</label>
                                    <textarea className="form-control" rows="3" name="recordatorio_colacion" value={form.recordatorio_colacion} onChange={handleChange} disabled={isSubmitting}></textarea>
                                </div>
                            </div>}

                            <div className="text-end">
                                <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={isSubmitting}>
                                    <i className="bi bi-save me-2"></i>
                                    {form.id ? "Actualizar" : "Guardar"}
                                </button>
                            </div>
                        </div>

                        <h6 className="fw-bold mb-3">Lista de recordatorios</h6>
                        {loading ? (
                            <div className="text-center py-3">Cargando...</div>
                        ) : recordatorios.length === 0 ? (
                            <div className="alert alert-info mb-0">No hay recordatorios registrados.</div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th>Estado</th>
                                            <th>Fechas</th>
                                            <th>Recordatorio</th>
                                            {MOSTRAR_RECORDATORIOS_VALORES && <th>Valores</th>}
                                            <th className="text-end">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recordatorios.map(item => (
                                            <tr key={item.id || `${item.fecha_actualizacion}-${item.observacion}`}>
                                                <td>
                                                    {(() => {
                                                        const status = getStatus(item);
                                                        return <span className={`badge ${status.className}`}>{status.label}</span>;
                                                    })()}
                                                </td>
                                                <td className="small">
                                                    {formatDate(item.fecha_desde)} al {formatDate(item.fecha_hasta)}
                                                </td>
                                                <td style={{ whiteSpace: "pre-wrap", minWidth: 220 }}>{item.observacion || "-"}</td>
                                                {MOSTRAR_RECORDATORIOS_VALORES && <td className="small" style={{ minWidth: 260 }}>
                                                    <div><strong>Turno:</strong> {item.recordatorio_valor_turno || "-"}</div>
                                                    <div><strong>Mov.:</strong> {item.recordatorio_movilizacion || "-"}</div>
                                                    <div><strong>Col.:</strong> {item.recordatorio_colacion || "-"}</div>
                                                </td>}
                                                <td className="text-end">
                                                    <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(item)} disabled={isSubmitting}>
                                                        Editar
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleEliminar(item)} disabled={isSubmitting || !item.id}>
                                                        Eliminar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalRecordatoriosPaciente;
